/**
 * Event door-prize drawing.
 *
 * The pool is everyone who REDEEMED a non-monetary reward (e.g. an "Entry into a Drawing" offer) for the
 * event at the redemption table — the `draw_event_winner` RPC picks a random winner from that set, deduped by
 * person and excluding prior winners. Winners are persisted in `event_drawing_winners` (auditable).
 */
import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

export interface DrawnWinner {
  person_key: string;
  name_snapshot: string | null;
  email_snapshot: string | null;
  voucher_id: UUID | null;
  pool_size: number | null;
}

export interface DrawingWinnerRow {
  id: UUID;
  event_id: UUID;
  person_key: string;
  name_snapshot: string | null;
  email_snapshot: string | null;
  pool: string | null;
  pool_size: number | null;
  prize_note: string | null;
  drawn_at: string;
}

/** Draw one random winner for the event's door-prize drawing. Returns [] when nobody is eligible yet. */
export async function drawEventWinner(
  eventId: UUID,
  opts: { prizeNote?: string; count?: number } = {},
): Promise<DrawnWinner[]> {
  const { data, error } = await supabase.rpc("draw_event_winner", {
    p_event_id: eventId,
    p_count: opts.count ?? 1,
    p_prize_note: opts.prizeNote?.trim() || null,
  });
  if (error) {
    console.error("Error drawing event winner:", error);
    throw error;
  }
  return (data as DrawnWinner[] | null) ?? [];
}

/** All winners drawn so far for this event, most recent first. */
export async function listDrawingWinners(
  eventId: UUID,
): Promise<DrawingWinnerRow[]> {
  const { data, error } = await supabase
    .from("event_drawing_winners")
    .select(
      "id, event_id, person_key, name_snapshot, email_snapshot, pool, pool_size, prize_note, drawn_at",
    )
    .eq("event_id", eventId)
    .order("drawn_at", { ascending: false });
  if (error) {
    console.error("Error loading drawing winners:", error);
    throw error;
  }
  return (data as DrawingWinnerRow[] | null) ?? [];
}
